import moment, { Moment } from "moment-timezone"
import { withContext as withContext_ } from "react-context-consumer-hoc"
import {
    CategoriesQueryProducts,
    CategoriesQueryProductsCategories,
    Maybe,
    MerchantInfoQueryMibLocations,
    ModifierGroupType,
    OrderQuery2Items,
} from "../../graphql/generated-types"
import { CartProduct, CartProductModifierTypeEnum } from "./types"
import { flatten } from "ramda"
import { isBrowser } from "../../utils/general"
import { getCookie, setCookie } from "../../utils/cookies"

export const withContext = withContext_

export const getDefaultEcoCheckboxValue = (
    locationInfo: MerchantInfoQueryMibLocations,
    products: CartProduct[],
    currentValue: boolean
) => (products.length > 0 ? currentValue : locationInfo.settings.isEcoFriendlyOptionDefaultChecked)

export const getPersistedOrderScheduledDate = (
    persistedOrderScheduledDate: Maybe<string>,
    locationInfo: MerchantInfoQueryMibLocations
): Maybe<Moment> => {
    if (persistedOrderScheduledDate) {
        const locationSettings = locationInfo.settings
        if (!locationSettings.acceptFutureOrdersForDelivery && !locationSettings.acceptFutureOrdersForPickUp) {
            return null
        }
        const orderScheduledDate = moment.tz(persistedOrderScheduledDate, locationInfo.timezone)
        if (orderScheduledDate.isBefore(moment.tz(locationInfo.timezone))) {
            return null
        }

        // Check whether delivery/pickup is disabled until some time - reset schedule date if is within disabled time
        const disabledUntil =
            locationInfo.settings.isDisabledConfig &&
            locationInfo.settings.isDisabledConfig.until &&
            (locationInfo.settings.isDisabledConfig.forPickup || locationInfo.settings.isDisabledConfig.forDelivery)

        if (disabledUntil) {
            const disabledUntilMoment = moment.tz(disabledUntil, locationInfo.timezone)
            if (orderScheduledDate.isBefore(disabledUntilMoment)) {
                return null
            }
        }

        return orderScheduledDate
    }
    return null
}

export const shouldRemoveCartItems = (itemsAddedToCartDate: Maybe<string>, products: CartProduct[]) =>
    (itemsAddedToCartDate && moment.duration(moment().diff(moment(itemsAddedToCartDate))).asHours() > 48) ||
    (products.length > 0 && !itemsAddedToCartDate)

export const shouldExtendItemsAddedDate = (itemsAddedToCartDate: Maybe<string>, products: CartProduct[]) =>
    products.length > 0 &&
    (!itemsAddedToCartDate ||
        (itemsAddedToCartDate && moment.duration(moment().diff(moment(itemsAddedToCartDate))).asHours() <= 48))

export const transformOrderItemToCartProduct = (
    item: OrderQuery2Items,
    categories: CategoriesQueryProductsCategories[]
): CartProduct => {
    const products = flatten<CategoriesQueryProducts>(categories.map((c) => c.products || []))
    const productData = products.find((p) => item.productId != null && p.productId === item.productId)

    return {
        id: item.id,
        // If we have don't have product data we want to handle such item as a 'custom' item
        productId: productData ? item.productId : null,
        ...(!productData ? { customPrice: item.basePrice } : {}),
        quantity: item.unit ? item.unitQuantity / 1000 : item.quantity,
        activeModifiers: item.modifiers.map((m) => {
            const isCustomModifier = !m.groupId && !m.productId
            const modGroup = !isCustomModifier ? item.modifierGroups.find((g) => g.id === m.groupId) : null

            // If is nested group
            const parentModifier = modGroup?.parentModifierId
                ? item.modifiers.find((pm) => pm.id === modGroup.parentModifierId)
                : null
            const parentModGroup = parentModifier
                ? item.modifierGroups.find((g) => g.id === parentModifier.groupId)
                : null

            return {
                productId: m.productId,
                customPrice: !productData || isCustomModifier ? m.basePrice : null,
                type:
                    modGroup?.type === ModifierGroupType.Option
                        ? CartProductModifierTypeEnum.option
                        : CartProductModifierTypeEnum.modifier,
                modifierGroupId: modGroup?.categoryGroupId,
                quantity: m.quantity || 1,
                ...(parentModifier && parentModGroup
                    ? {
                          parentModifierGroupType:
                              parentModGroup.type === ModifierGroupType.Option
                                  ? CartProductModifierTypeEnum.option
                                  : CartProductModifierTypeEnum.modifier,
                          parentModifierId: parentModifier.id,
                          parentModifierGroupId: parentModGroup.categoryGroupId,
                      }
                    : {}),
            }
        }),
        note: item.note,
        orderItemPricings: item.pricings,
        orderItemTaxes: item.taxes,
        isExistingOrderItem: true,
    }
}

export const GOOGLE_ORDERING_TOKEN = "rwg_token"
export const storeGoogleOrderingToken = () => {
    if (isBrowser && window.location.search.includes(`${GOOGLE_ORDERING_TOKEN}=`)) {
        const token = window.location.search.split(`${GOOGLE_ORDERING_TOKEN}=`)[1]

        if (token) {
            const domain = window.location.hostname
            setCookie(GOOGLE_ORDERING_TOKEN, token, { maxAge: 2592000, domain: domain, path: "/" })
        }
    }
}

export const sendGoogleOrderingData = async () => {
    const partnerId = "20001872"
    const endpoint = "https://www.google.com/maps/conversion/collect"
    const token = getCookie(GOOGLE_ORDERING_TOKEN)

    if (token) {
        return fetch(endpoint, {
            method: "POST",
            body: JSON.stringify({
                conversion_partner_id: partnerId,
                rwg_token: token,
            }),
        })
    }
}

export const isGoogleOrderingSandbox = () => getCookie(GOOGLE_ORDERING_TOKEN)
